import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  useMediaQuery,
  Typography,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';
import BaseButton from '../../Buttons/BaseButton';
import _get from 'lodash/get';
import clsx from 'clsx';

import GameContext from '../../../Contexts/GameContext';
import { ContractSelectedContext } from '../../../Contexts/ContractsContext';
import { UserContext } from '../../../Contexts/UserContext';

import BaseDialog from '../../Dialogs/BaseDialog';
import GameIframe from './GameIframe';
import CampaignConditionsList from './CampaignConditionsList';

import { config } from '../../../Configs';
import { TriviaCopa60TermsLink } from '../../../Configs/Links';
import { ValidateCampaignAPI } from '../../../API/Campaigns/CampaignAPI';
import { ALERT_TYPE } from '../../Alerts/alert_enums';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { joinNames } from '../../../Utils/Format/Names';

const BASE_CONDITIONS = [
  {
    code: 'NOT_LOGGED_IN',
    message: 'Necesitas iniciar sesión con una cuenta'
  },
  {
    code: 'DIGITAL_INVOICE_NOT_ACTIVATED_IN_CAMPAIGN',
    message: '¡Activa tu Factura Digital!',
    subtitle:
      'Recuerda que participan las activaciones realizadas durante el periodo de la campaña.'
  },
  {
    code: 'INVOICE_NOT_PAID',
    message:
      'Debes tener factura de los períodos requeridos pagada a través del Portal de Clientes'
  }
];

const buildConditions = (breachedConditions = []) =>
  BASE_CONDITIONS.map(base => {
    const breached = breachedConditions.find(c => c.code === base.code);
    return {
      code: base.code,
      message: base.message,
      subtitle: base.subtitle,
      met: !breached
    };
  });

const getValidationErrorState = response => {
  const errorResponse = response.error && response.error.response;
  const errorData = errorResponse && errorResponse.data;
  const errors = errorData && errorData.errors;
  const breachedConditions = errors && errors.breachedConditions;

  if (Array.isArray(breachedConditions) && breachedConditions.length > 0) {
    return {
      conditions: buildConditions(breachedConditions),
      campaignAvailable: true
    };
  }
  if (errors && errors.base === 'Autenticación inválida') {
    return {
      conditions: buildConditions([
        {
          code: 'NOT_LOGGED_IN',
          message: 'Necesitas iniciar sesión para participar'
        }
      ]),
      campaignAvailable: true
    };
  }
  if (errors && errors.reason) {
    return { conditions: [], campaignAvailable: false };
  }
  return {
    conditions: buildConditions(BASE_CONDITIONS),
    campaignAvailable: true
  };
};

const Game = ({ setOpenGame }) => {
  // * HOOKS
  const classes = useStyles();
  const smallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const selectedContract = useContext(ContractSelectedContext);
  const currentUser = useContext(UserContext);
  const setGameLoading = useContext(GameContext);
  const setAlert = useContext(AlertsDispatchContext);

  // * STATE HOOKS
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(config.campaign.game.url);
  const [showGame, setShowGame] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [allConditionsMet, setAllConditionsMet] = useState(false);
  const [conditionsReady, setConditionsReady] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [campaignAvailable, setCampaignAvailable] = useState(true);

  const isMountedRef = useRef(true);

  const authToken = _get(currentUser, 'token');
  const contractId = _get(selectedContract, 'id');

  const triviaOrigin = config.campaign.game.url;

  const userData = useMemo(() => {
    if (!currentUser || !selectedContract) {
      return {};
    }

    const fullName = joinNames([
      _get(currentUser, 'firstName'),
      _get(currentUser, 'lastName')
    ]);

    const participationData = {
      contractNumber: contractId.toString(),
      fullName,
      city: _get(selectedContract, 'city'),
      email: _get(currentUser, 'email')
    };

    if (_get(currentUser, 'phoneValidated')) {
      participationData.phone = _get(currentUser, 'phone');
    }

    return participationData;
  }, [contractId, currentUser, selectedContract]);

  const openGame = useCallback(() => {
    setLoading(true);
    setShowGame(true);
  }, []);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleDialogExited = useCallback(() => {
    setOpenGame(false);
    if (isMountedRef.current) {
      setLoading(false);
      setShowGame(false);
      setConditions([]);
      setAllConditionsMet(false);
      setConditionsReady(false);
      setTermsAccepted(false);
      setCampaignAvailable(true);
    }
  }, [setOpenGame]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleMessage = async event => {
      if (event.origin !== triviaOrigin) {
        return;
      }

      if (!event.data) {
        return;
      }

      if (event.data.type === 'trivia:ready') {
        const iframe = document.getElementById('trivia-frame');
        if (!iframe || !iframe.contentWindow) {
          return;
        }

        iframe.contentWindow.postMessage(
          {
            type: 'trivia:init',
            ...userData
          },
          triviaOrigin
        );
      }

      if (event.data.type === 'trivia:ack') {
        if (event.data.status === 'ok') {
          if (!isMountedRef.current) {
            return;
          }

          await ValidateCampaignAPI({
            reason: config.campaign.name || 'NO-CAMPAIGN',
            token: authToken,
            contractId: authToken && contractId ? contractId : null,
            dryRun: false
          });
        } else {
          setAlert({
            type: ALERT_TYPE.ERROR,
            message:
              'Ocurrió un error al cargar el juego, intentalo de nuevo más tarde.'
          });
          handleDialogExited();
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    triviaOrigin,
    userData,
    setAlert,
    handleDialogExited,
    contractId,
    authToken
  ]);

  const hasInitiatedValidation = useRef(false);

  useEffect(() => {
    if (hasInitiatedValidation.current) {
      return;
    }

    hasInitiatedValidation.current = true;

    const validateCampaign = async () => {
      setLoading(true);
      if (typeof setGameLoading === 'function') {
        setGameLoading(true);
      }

      try {
        const response = await ValidateCampaignAPI({
          reason: config.campaign.name || 'NO-CAMPAIGN',
          token: authToken,
          contractId: authToken && contractId ? contractId : null,
          dryRun: true
        });

        if (!isMountedRef.current) {
          return;
        }

        if (typeof setGameLoading === 'function') {
          setGameLoading(false);
        }
        setLoading(false);

        if (response.success) {
          setConditions(buildConditions([]));
          setAllConditionsMet(true);
        } else {
          const errorState = getValidationErrorState(response);
          setConditions(errorState.conditions);
          setCampaignAvailable(errorState.campaignAvailable);
          setAllConditionsMet(false);
        }

        setConditionsReady(true);
        setDialogOpen(true);
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }

        if (typeof setGameLoading === 'function') {
          setGameLoading(false);
        }
        setLoading(false);
        setConditions(buildConditions(BASE_CONDITIONS));
        setAllConditionsMet(false);
        setConditionsReady(true);
        setDialogOpen(true);
      }
    };

    validateCampaign();
  }, [authToken, contractId, setGameLoading, setAlert, openGame]);

  const renderActions = () => {
    if (!campaignAvailable) {
      return <></>;
    }

    return (
      <BaseButton
        disabled={!allConditionsMet || !termsAccepted || loading}
        onClick={() => {
          setUrl(config.campaign.game.url);
          openGame();
        }}
      >
        Participar
      </BaseButton>
    );
  };

  const dialogTitle = useMemo(() => {
    if (showGame) {
      return '';
    }

    if (!campaignAvailable) {
      return 'Campaña no disponible';
    }

    return 'Condiciones para participar';
  }, [showGame, campaignAvailable]);

  return (
    <>
      <BaseDialog
        open={dialogOpen}
        fullScreen={smallScreen}
        handleClose={handleClose}
        onExited={handleDialogExited}
        disableBackdropClick
        maxWidth={showGame ? false : 'xs'}
        classes={{
          paper: clsx(
            classes.paper,
            smallScreen && classes.fullWidth,
            !campaignAvailable && classes.paperUnavailable
          )
        }}
        title={dialogTitle}
        loading={loading}
        content={() => (
          <>
            {conditionsReady && showGame === false && (
              <>
                {campaignAvailable ? (
                  <>
                    {currentUser && (
                      <Typography variant="body1" className={classes.greeting}>
                        Hola, {_get(currentUser, 'firstName')}
                      </Typography>
                    )}
                    <Typography variant="body2" className={classes.description}>
                      Gracias por tu interés en participar. Para poder hacerlo
                      debes cumplir estos requisitos:
                    </Typography>
                    <CampaignConditionsList
                      currentUser={currentUser}
                      conditions={conditions}
                      contractId={contractId}
                      digitalInvoice={_get(selectedContract, 'digitalInvoice')}
                    />
                  </>
                ) : (
                  <Typography variant="body2" className={classes.description}>
                    La campaña no está disponible en este momento.
                  </Typography>
                )}
                {currentUser && allConditionsMet && (
                  <FormControlLabel
                    className={classes.terms}
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Acepto los{' '}
                        <a
                          href={TriviaCopa60TermsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >
                          Términos y Condiciones
                        </a>{' '}
                        de la campaña.
                      </Typography>
                    }
                  />
                )}
                {campaignAvailable && !allConditionsMet && (
                  <Typography variant="caption" className={classes.termsStatic}>
                    Al participar aceptas los{' '}
                    <a
                      href={TriviaCopa60TermsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Términos y Condiciones
                    </a>{' '}
                    de la campaña.
                  </Typography>
                )}
              </>
            )}
            {showGame && (
              <GameIframe loading={loading} setLoading={setLoading} url={url} />
            )}
          </>
        )}
        actions={showGame ? null : renderActions}
        contentStyle={clsx(classes.content, showGame && classes.gameContent)}
        titleStyle={clsx(classes.title, showGame && classes.gameTitle)}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  paper: {
    width: '80%'
  },
  paperUnavailable: {
    '&&': {
      overflowY: 'hidden'
    }
  },
  fullWidth: {
    width: '100%'
  },
  gameContent: {
    padding: theme.spacing(1),
    height: '100%',
    width: '100%'
  },
  content: {
    minHeight: 'auto',
    '&&': {
      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
      }
    }
  },
  greeting: {
    paddingBottom: theme.spacing(1),
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(0.5)
    }
  },
  description: {
    paddingBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(1)
    }
  },
  terms: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginLeft: 0,
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0.5)
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline'
    }
  },
  termsStatic: {
    display: 'block',
    paddingTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0.5)
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline'
    }
  },
  title: {
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3)
    }
  },
  gameTitle: {
    zIndex: 4,
    top: 0,
    left: 0,
    position: 'absolute',
    minHeight: 'auto',
    padding: 0,
    backgroundColor: 'transparent',
    '& button': {
      backgroundColor: 'white',
      padding: theme.spacing(0.75)
    },
    '& button:hover': {
      backgroundColor: 'white'
    }
  }
}));

export default Game;
